import { useCallback, useMemo } from 'react';
import uuid from 'uuid/v1';
import { useIntl } from 'modules/taboola-common-frontend-modules/i18n';
import { CHAT_AGENT_ACTION_TYPE, CHAT_AGENT_MESSAGE_SOURCE } from '../config/chatAgentActionType';
import { CHAT_AGENT_COMPONENT_TYPE } from '../config/chatAgentComponentType';
import { CHAT_DIALOG_MESSAGE_PROP_PATH } from '../config/chatDialogMessagePropPath';
import { formatActionButton } from '../utils/actionButtonUtils';
import { formatAction } from '../utils/eventActionsUtils';

export const useReportingTools = ({ sendResponseToChatAgent }) => {
    const { formatMessage } = useIntl();

    const downloadCsv = useCallback(
        async ({ csvData, baseFilename }) => {
            if (!csvData || typeof csvData !== 'string') {
                return false;
            }

            if (csvData.length > 50 * 1024 * 1024) {
                return false;
            }

            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
            const filename = `${baseFilename || 'report'}_${timestamp}.csv`;

            const buttonConfig = formatActionButton({
                icon: 'page',
                title: formatMessage({ id: 'abby.downloadCsv.button', defaultMessage: 'Download CSV' }),
                postIcon: 'download',
                flow: {
                    actions: [
                        formatAction({
                            type: CHAT_AGENT_ACTION_TYPE.DOWNLOAD_FILE,
                            data: {
                                rawData: JSON.stringify({
                                    filename,
                                    content: csvData,
                                    mimeType: 'text/csv',
                                }),
                            },
                        }),
                    ],
                },
            });

            const message = {
                id: uuid(),
                componentData: {
                    value: buttonConfig,
                    componentName: CHAT_AGENT_COMPONENT_TYPE.ACTION_BUTTON,
                },
                flows: [
                    {
                        actions: [
                            formatAction({
                                type: CHAT_AGENT_ACTION_TYPE.MESSAGE,
                                data: {
                                    value: buttonConfig,
                                    source: CHAT_AGENT_MESSAGE_SOURCE.AGENT,
                                },
                            }),
                        ],
                    },
                ],
            };

            sendResponseToChatAgent({ [CHAT_DIALOG_MESSAGE_PROP_PATH]: message, actions: [] });

            return true;
        },
        [sendResponseToChatAgent, formatMessage]
    );

    return useMemo(
        () => ({
            downloadCsv,
        }),
        [downloadCsv]
    );
};
